import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Bell } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Card } from './ui/card';
import { useNavigate } from 'react-router-dom';

const NotificationDropdown = ({ profile }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (profile?.id) {
      fetchNotifications();
      
      // Realtime subscription for INSERT and UPDATE
      const channel = supabase
        .channel(`notifications-${profile.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${profile.id}`
          },
          () => {
            fetchNotifications();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${profile.id}`
          },
          () => {
            fetchNotifications();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [profile?.id]);

  const fetchNotifications = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get('/api/notifications', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      const notifs = response.data.notifications || [];
      setNotifications(notifs);
      
      // Calculate unread based on read_at being null
      const unread = notifs.filter(n => !n.read_at).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (session?.session?.access_token) {
        await api.post(
          `/api/notifications/${notification.id}/read`,
          {},
          {
            headers: {
              Authorization: `Bearer ${session.session.access_token}`
            }
          }
        );
      }

      // Route to relevant page based on notification type
      const type = notification.type || '';
      if (type.startsWith('interview_')) {
        navigate('/dashboard?showInterview=true');
      } else if (type === 'new_message') {
        const senderId = notification.payload?.sender_id;
        navigate(senderId ? `/messages?user=${senderId}` : '/messages');
      } else if (type === 'new_application' && (notification.payload?.job_slug || notification.payload?.job_id)) {
        // "Applicants waiting" nudge (and any alert that names the job): open that job's applicants
        navigate(`/jobs/${encodeURIComponent(notification.payload.job_slug || notification.payload.job_id)}`);
      } else if (type === 'matching_job_available') {
        const jobSlug = notification.payload?.job_slug;
        navigate(jobSlug ? `/jobs/${jobSlug}` : '/dashboard');
      } else if (['new_application', 'application_withdrawn', 'new_bid', 'bid_withdrawn',
                   'application_viewed', 'bid_viewed', 'profile_viewed', 'gig_filled',
                   'verification_approved', 'verification_rejected',
                   'credit_purchase', 'referral_reward', 'unlimited_expiring',
                   'featured_expired', 'job_expired', 'gig_expired',
                   'post_removed', 'gig_removed', 'account_banned'].includes(type)) {
        navigate('/dashboard');
      } else {
        // Fallback for any unhandled notification type
        navigate('/dashboard');
      }

      setOpen(false);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs">
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <Card className="fixed md:absolute right-2 md:right-0 top-16 md:top-12 w-[calc(100vw-1rem)] md:w-96 max-w-md z-50 shadow-lg">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">{t('notifications.title')}</h3>
                  {unreadCount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {unreadCount} {t('notifications.unread')}
                    </p>
                  )}
                </div>
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const { data: session } = await supabase.auth.getSession();
                        if (session?.session?.access_token) {
                          await api.post(
                            '/api/notifications/mark-all-read',
                            {},
                            {
                              headers: {
                                Authorization: `Bearer ${session.session.access_token}`
                              }
                            }
                          );
                          fetchNotifications();
                        }
                      } catch (error) {
                        console.error('Failed to mark all as read:', error);
                      }
                    }}
                    className="text-xs"
                    data-testid="mark-all-read-btn"
                  >
                    {t('notifications.markAllRead')}
                  </Button>
                )}
              </div>
            </div>
            <ScrollArea className="h-96 max-h-[calc(100dvh-12rem)]">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Bell size={48} className="mx-auto mb-2 opacity-50" />
                  <p>{t('notifications.noNotifications')}</p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-4 hover:bg-muted/50 cursor-pointer transition-colors ${
                        !notif.read_at ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => handleNotificationClick(notif)}
                      data-testid="notification-item"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm break-words">{notif.title || 'Notification'}</p>
                          <p className="text-sm text-muted-foreground mt-1 break-words">
                            {notif.body || notif.message || 'You have a new notification'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {getTimeAgo(notif.created_at)}
                          </p>
                        </div>
                        {!notif.read_at && (
                          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </Card>
        </>
      )}
    </div>
  );
};

export default NotificationDropdown;